/**
 * YearView — a calendar year folded out of the codex on read. A hero year, an
 * honest one-line summary, and the moments that touched it (each a gold "link"
 * chip that opens its source entry). A quiet year — nothing in the Vault folds
 * to it — gets a calm stub, never a fabricated event. De-boxed, editorial.
 */
import { DetailHeader, Rule } from './chrome';
import { ShareButton } from './ShareButton';
import { yearLink } from '../lib/share';
import type { YearView as YearViewModel } from '../lib/types';

export const YearView = ({
  view,
  onOpen,
  onBack,
  backLabel = 'Back',
  trail,
}: {
  view: YearViewModel;
  onOpen: (id: string) => void;
  onBack: () => void;
  /** Label for the back button — names where this step returns to. */
  backLabel?: string;
  /** The full ancestor path for the "where am I" breadcrumb. */
  trail?: string[];
}) => (
  <div className="screen detail-screen entry-screen year-view">
    <DetailHeader
      onBack={onBack}
      backLabel={backLabel}
      trail={trail}
      right={
        <ShareButton
          ghost
          label="Share"
          moment={{
            title: `COYG — ${view.year}`,
            text: view.quiet
              ? `${view.year} in Arsenal's Vault.`
              : view.summary,
            url: yearLink(view.year),
            card: {
              eyebrow: 'FROM THE VAULT',
              headline: view.year,
              detail: view.quiet ? 'A quieter year in Arsenal’s story.' : view.summary,
              stamp: 'CLUB MEMORY · COYG.ORG',
            },
          }}
        />
      }
    />

    <header className="player-head year-head">
      <span className="player-head-num" aria-hidden="true">
        {view.year}
      </span>
      <span className="player-kicker">From the Vault</span>
      <h2 className="player-title">{view.year}</h2>
    </header>

    {view.quiet ? (
      <p className="player-lede year-quiet">A quieter year — nothing in the Vault yet.</p>
    ) : (
      <>
        {view.summary && <p className="player-lede">{view.summary}</p>}
        {view.highlights.length > 0 && (
          <>
            <Rule label="Moments" accent="#EF0107" />
            <ul className="year-highlights">
              {view.highlights.map((h, i) => (
                <li key={`${h.entryId}-${i}`} className="year-highlight">
                  <button className="honour-chip honour-chip--link" onClick={() => onOpen(h.entryId)}>
                    {h.entryTitle} <span aria-hidden="true">→</span>
                  </button>
                  <span className="year-highlight-event">{h.event}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </>
    )}
  </div>
);
